import { useEffect } from 'react'
import { Alert, Form, Input, InputNumber, Modal, Select } from 'antd'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { useAuth } from '@/shared/auth/useAuth'
import { submitOnValid } from '@/shared/lib/submitOnValid'
import {
  VENUE_FUNDINGS,
  VENUE_KINDS,
  createVenue,
  updateVenue,
  type Venue,
  type VenueFunding,
  type VenueKind,
} from './venueApi'

interface VenueFormModalProps {
  open: boolean
  /** `null` = yangi ob'ekt */
  venue: Venue | null
  onClose: () => void
  /** Yaratilgandan keyin (masalan, kartochkaga o'tish uchun) */
  onCreated?: (venue: Venue) => void
}

interface FormValues {
  code: string
  name: string
  kind: VenueKind
  timezone?: string
  contactName?: string
  contactPhone?: string
  funding: VenueFunding
  piiRetentionDays?: number | null
  journalRetentionDays?: number | null
}

/**
 * Ob'ekt yaratish/tahrirlash.
 *
 * Saqlash muddatlari (`piiRetentionDays`/`journalRetentionDays`) ixtiyoriy —
 * bo'sh qoldirilsa backend sukut qiymatni qo'llaydi (365/1095 kun). Buni
 * MAJBURIY qilish har ob'ekt yaratishda huquqiy qaror qabul qilishga
 * majburlardi (`VenueDraft.java` javadoc).
 */
export function VenueFormModal({ open, venue, onClose, onCreated }: VenueFormModalProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const queryClient = useQueryClient()
  const tenantZone = useAuth().user?.tenant.timezone ?? undefined

  useEffect(() => {
    if (!open) return
    form.setFieldsValue(
      venue
        ? {
            code: venue.code,
            name: venue.name,
            kind: venue.kind,
            timezone: venue.timezone,
            contactName: venue.contactName ?? undefined,
            contactPhone: venue.contactPhone ?? undefined,
            funding: venue.funding,
            piiRetentionDays: venue.piiRetentionDays,
            journalRetentionDays: venue.journalRetentionDays,
          }
        : {
            kind: 'HOTEL',
            funding: 'VENUE',
            timezone: tenantZone,
          },
    )
  }, [open, venue, form, tenantZone])

  const save = useMutation({
    mutationFn: async (values: FormValues) => {
      const draft = {
        code: values.code.trim(),
        name: values.name.trim(),
        kind: values.kind,
        timezone: values.timezone?.trim() || tenantZone || null,
        contactName: values.contactName?.trim() || null,
        contactPhone: values.contactPhone?.trim() || null,
        funding: values.funding,
        piiRetentionDays: values.piiRetentionDays ?? null,
        journalRetentionDays: values.journalRetentionDays ?? null,
      }
      return venue ? updateVenue(venue.id, draft) : createVenue(draft)
    },
    onSuccess: (saved) => {
      void queryClient.invalidateQueries({ queryKey: queryKeys.venues.all })
      if (venue) void queryClient.invalidateQueries({ queryKey: queryKeys.venues.detail(venue.id) })
      form.resetFields()
      onClose()
      if (!venue) onCreated?.(saved)
    },
  })

  /** Bekor qilib chiqish — xato xabari ham qolmasin: keyingi ochilishda u
      eski va begona bo'lardi */
  const close = () => {
    save.reset()
    onClose()
  }

  return (
    <Modal
      open={open}
      title={venue ? t('venue.editTitle') : t('venue.createTitle')}
      onCancel={close}
      onOk={() => submitOnValid(form, (values) => save.mutate(values))}
      confirmLoading={save.isPending}
      okText={venue ? t('common.save') : t('operation.create')}
      cancelText={t('common.cancel')}
      destroyOnHidden
      width={560}
    >
      {/* Xato AYNAN shu yerda qoladi — sahifa bannerida emas: oyna ochiqligicha
          turadi va niqob ortidagi xabarni operator umuman ko'rmasdi */}
      {save.isError ? (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={translateError(save.error, t)}
        />
      ) : null}

      <Form<FormValues> form={form} layout="vertical" preserve={false} disabled={save.isPending}>
        <Form.Item
          name="code"
          label={t('venue.code')}
          extra={t('venue.codeHint')}
          rules={[
            { required: true, message: t('venue.codeRequired') },
            {
              pattern: /^[a-z0-9][a-z0-9-]{1,30}[a-z0-9]$/,
              message: t('venue.codeFormat'),
            },
          ]}
        >
          <Input autoFocus autoComplete="off" disabled={venue !== null} />
        </Form.Item>

        <Form.Item name="name" label={t('venue.name')} rules={[{ required: true, message: t('venue.nameRequired') }]}>
          <Input />
        </Form.Item>

        <Form.Item name="kind" label={t('venue.kind')} rules={[{ required: true }]}>
          <Select options={VENUE_KINDS.map((k) => ({ value: k, label: t(`venueKind.${k}`) }))} />
        </Form.Item>

        <Form.Item name="timezone" label={t('venue.timezone')} extra={t('venue.timezoneHint')}>
          <Input placeholder={tenantZone ?? 'Asia/Kabul'} />
        </Form.Item>

        <Form.Item name="contactName" label={t('venue.contactName')}>
          <Input />
        </Form.Item>

        <Form.Item name="contactPhone" label={t('venue.contactPhone')}>
          <Input autoComplete="off" inputMode="tel" />
        </Form.Item>

        <Form.Item name="funding" label={t('venue.funding')} extra={t('venue.fundingHint')}>
          <Select options={VENUE_FUNDINGS.map((f) => ({ value: f, label: t(`venueFunding.${f}`) }))} />
        </Form.Item>

        <Form.Item
          name="piiRetentionDays"
          label={t('venue.piiRetentionDays')}
          extra={t('venue.piiRetentionHint')}
        >
          <InputNumber min={1} max={3650} style={{ width: '100%' }} placeholder="365" />
        </Form.Item>

        <Form.Item
          name="journalRetentionDays"
          label={t('venue.journalRetentionDays')}
          extra={t('venue.journalRetentionHint')}
        >
          <InputNumber min={1} max={3650} style={{ width: '100%' }} placeholder="1095" />
        </Form.Item>
      </Form>
    </Modal>
  )
}
